'use client';

import DirectusImage from '@/components/shared/DirectusImage';
import Container from '@/components/ui/container';
import Headline from '@/components/ui/Headline';
import { setAttr } from '@directus/visual-editing';
import React, { useMemo } from 'react';

interface PhotoCollageImage {
	id: string;
	caption?: string;
	sort?: number;
	directus_files_id: any; // Can be string (uuid) or object depending on Directus depth
}

interface PhotoCollageData {
	id: string;
	title?: string;
	background_color?: string;
	images?: PhotoCollageImage[];
}

interface PhotoCollageProps {
	data: PhotoCollageData;
	blockId?: string;
	itemId?: string;
}

// Helper to chunk the images array into sub-arrays of a specific size (e.g. 2 for mobile horizontal masonry columns)
const chunkArray = <T,>(arr: T[], size: number): T[][] => {
	const chunked: T[][] = [];
	for (let i = 0; i < arr.length; i += size) {
		chunked.push(arr.slice(i, i + size));
	}

	return chunked;
};

const PhotoCollage = ({ data, itemId }: PhotoCollageProps) => {
	const { title, background_color, images, id } = data;
	const refId = itemId || id;

	// Sort explicitly by the Directus 'sort' property if available
	const sortedImages = useMemo(() => {
		if (!images) return [];

		return [...images].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
	}, [images]);

	// For mobile, we chunk images into pairs of 2 so that each "column" has 2 images,
	// allowing for a horizontal swipeable masonry-esque layout.
	const mobileColumns = useMemo(() => {
		return chunkArray(sortedImages, 2);
	}, [sortedImages]);

	// Resolve the UUID safely depending on if the API returns just the string or the deep object
	const getImageId = (fileData: any) => {
		if (!fileData) return null;
		if (typeof fileData === 'string') return fileData;

		return fileData.id;
	};

	if (!sortedImages.length) {
		return null;
	}

	const bgClass =
		background_color === 'gray'
			? 'bg-gray'
			: background_color === 'blue'
				? 'bg-blue text-white'
				: background_color === 'light_blue'
					? 'bg-blue-light'
					: 'bg-white'; // default to white

	return (
		<section className={`py-8 md:py-12 ${bgClass}`}>
			<Container>
				{title && title.trim() !== '' && (
					<div className="mb-8">
						<Headline
							headline={title}
							data-directus={setAttr({
								collection: 'block_photo_collage',
								item: refId,
								fields: 'title',
								mode: 'popover',
							})}
						/>
					</div>
				)}

				<div
					data-directus={setAttr({
						collection: 'block_photo_collage',
						item: refId,
						fields: 'images',
						mode: 'modal',
					})}
				>
					{/* MOBILE LAYOUT: Swiping Horizontal Masonry (< md) */}
					<div className="flex md:hidden overflow-x-auto snap-x snap-mandatory gap-4 pb-4 px-2 -mx-2">
						{mobileColumns.map((colGroup, colIndex) => (
							<div key={`mob-col-${colIndex}`} className="flex flex-col gap-4 flex-shrink-0 w-64 xs:w-72 snap-center">
								{colGroup.map((img) => {
									const uuid = getImageId(img.directus_files_id);
									if (!uuid) return null;

									return (
										<figure
											key={img.id}
											className="relative w-full rounded-lg overflow-hidden bg-gray-50 flex flex-col"
										>
											{/* Next/image intrinsic container */}
											<div className="relative w-full flex-grow">
												<DirectusImage
													uuid={uuid}
													alt={img.caption || `Photo Collage Image ${img.id}`}
													width={600}
													height={800}
													className="w-full h-auto object-cover rounded-md"
												/>
											</div>
											{img.caption && (
												<figcaption className="text-sm text-gray-600 p-2 italic bg-white/80 backdrop-blur-sm border-t border-gray-100">
													{img.caption}
												</figcaption>
											)}
										</figure>
									);
								})}
							</div>
						))}
					</div>

					{/* DESKTOP LAYOUT: Traditional CSS Columns Masonry (>= md) */}
					<div className="hidden md:block columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
						{sortedImages.map((img) => {
							const uuid = getImageId(img.directus_files_id);
							if (!uuid) return null;

							return (
								<figure
									key={img.id}
									className="break-inside-avoid relative rounded-lg overflow-hidden bg-gray-50 transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl group"
								>
									<DirectusImage
										uuid={uuid}
										alt={img.caption || `Photo Collage Image ${img.id}`}
										width={800}
										height={1000}
										className="w-full h-auto rounded-lg object-cover"
									/>
									{img.caption && (
										<figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-white text-sm translate-y-full group-hover:translate-y-0 transition-transform duration-300">
											{img.caption}
										</figcaption>
									)}
								</figure>
							);
						})}
					</div>
				</div>
			</Container>
		</section>
	);
};

export default PhotoCollage;
